import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from "./components/Layout.tsx";
import { Dashboard } from "./pages/Dashboard.tsx";
import { AdminDashboard } from "./pages/AdminDashboard.tsx";
import { MarketDetail } from "./pages/MarketDetail.tsx";
import { MarketChat } from './pages/MarketChat.tsx';
import { UserLogin } from './pages/UserLogin.tsx';
import { AdminLogin } from './pages/AdminLogin.tsx';
import { UserRegistration } from './pages/UserRegistration.tsx';
import { AdminManagement } from './pages/AdminManagement.tsx';
import { AccountSettings } from './pages/AccountSettings.tsx';
import { ResetPassword } from './pages/ResetPassword.tsx';
import { UpdatePassword } from './pages/UpdatePassword.tsx';
import { WalletManagement } from './pages/WalletManagement.tsx';
import { UserPortfolio } from './pages/UserPortfolio.tsx';
import { AdminMarketRequests } from './pages/AdminMarketRequests.tsx';
import { ProtectedRoute } from './components/ProtectedRoute.tsx';
import { SystemSettings } from './pages/SystemSettings.tsx';
import { AdminMarkets } from './pages/AdminMarkets.tsx';
import { AdminWalletManagement } from './pages/AdminWalletManagement.tsx';
import { AdminNotifications } from './pages/AdminNotifications.tsx';
import { AdminTransactions } from './pages/AdminTransactions.tsx';
import { AdminWithdrawals } from './pages/AdminWithdrawals.tsx';
import { AdminFICA } from './pages/AdminFICA.tsx';
import { AdminPartners } from './pages/AdminPartners.tsx';
import { AdminManage } from './pages/AdminManage.tsx';
import { AdminMarketDetail } from './pages/AdminMarketDetail.tsx';
import AdminBulkEmail from './pages/AdminBulkEmail.tsx';
import { AdminUserProfile } from './pages/AdminUserProfile.tsx';
import { CreateMarketForm } from './components/forms/CreateMarketForm.tsx';
import { CreateMarket } from './pages/CreateMarket.tsx';
import { CreatePrivateFixedMarket } from './pages/CreatePrivateFixedMarket.tsx';
import { CreatePrivateOpenMarket } from './pages/CreatePrivateOpenMarket.tsx';
import { MarketVisibilitySelection } from './pages/MarketVisibilitySelection.tsx';
import { MarketTypeSelection } from './pages/MarketTypeSelection.tsx';
import { ContactSupportPage } from './pages/ContactSupportPage.tsx';
import { TermsAndConditions } from './pages/TermsAndConditions.tsx';
import FAQs from './pages/FAQs.tsx';
import AboutUs from './pages/AboutUs.tsx';
import { MarketStandards } from './pages/MarketStandards.tsx';
import { UserNotificationSettings } from './pages/UserNotificationSettings.tsx';
import { DepositResult } from './pages/DepositResult.tsx';
import { AffiliateDashboardPage } from './pages/AffiliateDashboard.tsx';
import { CreatorDashboardPage } from './pages/CreatorDashboardPage.tsx';
import { AuthVerify } from './pages/AuthVerify.tsx';
import { AuthRedirect } from './pages/AuthRedirect.tsx';
import { MonitoringDashboard } from './components/MonitoringDashboard.tsx';
import { AdminMetrics } from './pages/AdminMetrics.tsx';
import { TrafficMonitorWrapper } from './components/TrafficMonitorWrapper.tsx';
import { SessionTimeoutProvider } from './components/SessionTimeoutProvider.tsx';
import DeveloperPortal from './pages/DeveloperPortal.tsx';
import ApiReference from './pages/ApiReference.tsx';
import QuickStart from './pages/QuickStart.tsx';
import CodeExamples from './pages/CodeExamples.tsx';
import WidgetSDK from './pages/WidgetSDK.tsx';
import { DeveloperRegistration } from './pages/DeveloperRegistration.tsx';
import { AdminDeveloperManagement } from './pages/AdminDeveloperManagement.tsx';
import { DeveloperDashboard } from './pages/DeveloperDashboard.tsx';
import { AdminSupport } from './pages/AdminSupport.tsx';
import { CompetitionTerms } from './pages/CompetitionTerms.tsx';
import { LeaderboardPage } from './pages/LeaderboardPage.tsx';
import { ChallengeLanding } from './pages/ChallengeLanding.tsx';
import { AdminFeesSettings } from './pages/AdminFeesSettings.tsx';


function App() {
  return (
    <Router>
      <SessionTimeoutProvider>
      <TrafficMonitorWrapper>
      <Routes>
        {/* Auth routes */}
        <Route path="/auth" element={<Layout />}>
          <Route path="login" element={<UserLogin />} />
          <Route path="register" element={<UserRegistration />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="verify" element={<AuthVerify />} />
          <Route path="redirect" element={<AuthRedirect />} />
        </Route>

        {/* Update password route - standalone without Layout for clean UI */}
        <Route path="/update-password" element={<UpdatePassword />} />

        {/* Auth confirm route - Supabase default route for email confirmations */}
        <Route path="/auth/confirm" element={<UpdatePassword />} />

        {/* Redirect old auth routes to new paths */}
        <Route path="/login" element={<Navigate to="/auth/login" replace />} />
        <Route path="/register" element={<Navigate to="/auth/register" replace />} />
        <Route path="/reset-password" element={<Navigate to="/auth/reset-password" replace />} />

        {/* Admin login remains separate */}
        <Route path="/admin/login" element={<AdminLogin />} />

        {/* Public Market Routes - Everyone can see the dashboard and market details */}
        <Route path="/" element={<Layout />}>
          <Route index element={<Navigate to="/markets" replace />} />
          <Route path="markets" element={<Dashboard />} />
          <Route path="leaderboard" element={<LeaderboardPage />} />
          <Route path="markets/:id" element={<MarketDetail />} />
          <Route path="contact-support" element={<ContactSupportPage />} />
          <Route path="terms" element={<TermsAndConditions />} />
          <Route path="faqs" element={<FAQs />} />
          <Route path="about" element={<AboutUs />} />
          <Route path="market-standards" element={<MarketStandards />} />
          <Route path="competition-terms" element={<CompetitionTerms />} />
          <Route path="challenge/:shortCode" element={<ChallengeLanding />} />
          <Route path="developers" element={<DeveloperPortal />} />
          <Route path="developers/register" element={<DeveloperRegistration />} />
          <Route path="developers/dashboard" element={<DeveloperDashboard />} />
          <Route path="developers/quickstart" element={<QuickStart />} />
          <Route path="developers/api-reference" element={<ApiReference />} />
          <Route path="developers/widget" element={<WidgetSDK />} />
          <Route path="developers/examples" element={<CodeExamples />} />
        </Route>

        {/* Protected user routes that require login */}
        <Route path="/chat" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route path="markets/:id" element={<MarketChat />} />
        </Route>

        <Route path="/account" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route path="settings" element={<AccountSettings />} />
          <Route path="wallet" element={<WalletManagement />} />
          <Route path="portfolio" element={<UserPortfolio />} />
          <Route path="create-market-form" element={<CreateMarketForm />} />
          <Route path="affiliate" element={<AffiliateDashboardPage />} />
          <Route path="creator" element={<CreatorDashboardPage />} />
        </Route>

        {/* Payment result routes */}
        <Route path="/wallet" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route path="deposit/result" element={<DepositResult />} />
        </Route>

        {/* Settings routes */}
        <Route path="/settings" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route path="notifications" element={<UserNotificationSettings />} />
        </Route>

        {/* Protected admin routes */}
        <Route path="/admin" element={
          <ProtectedRoute requireAdmin>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="metrics" element={<AdminMetrics />} />
          <Route path="monitoring" element={<MonitoringDashboard />} />
          <Route path="market/:id" element={<AdminMarketDetail />} />
          <Route path="manage" element={<AdminManagement />} />
          <Route path="market-requests" element={<AdminMarketRequests />} />
          <Route path="settings" element={<SystemSettings />} />
          <Route path="fees" element={<AdminFeesSettings />} />
          <Route path="create-market" element={<CreateMarket />} />
          <Route path="markets" element={<AdminMarkets />} />
          <Route path="wallet" element={<AdminWalletManagement />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="transactions" element={<AdminTransactions />} />
          <Route path="withdrawals" element={<AdminWithdrawals />} />
          <Route path="fica" element={<AdminFICA />} />
          <Route path="partners" element={<AdminPartners />} />
          <Route path="site" element={<AdminManage />} />
          <Route path="create-market" element={<CreateMarket />} />
          <Route path="bulk-email" element={<AdminBulkEmail />} />
          <Route path="support" element={<AdminSupport />} />
          <Route path="users/:id" element={<AdminUserProfile />} />
          <Route path="developers" element={<AdminDeveloperManagement />} />
        </Route>

        {/* Catch all redirect */}
        <Route path="*" element={<Navigate to="/markets" replace />} />
      </Routes>
      </TrafficMonitorWrapper>
      </SessionTimeoutProvider>
    </Router>
  );
}

export default App;